import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { reportKeys } from "../utils/queryKeys";

export interface UserBreakdown {
  userId: string;
  name: string;
  totalMinutes: number;
  billableMinutes?: number;
  cost: number;
}

export interface ProjectBreakdown {
  projectId: string;
  name: string;
  totalMinutes: number;
}

export interface ClientProjectBreakdown {
  clientId: string;
  clientName: string;
  projectId: string;
  projectName: string;
  totalMinutes: number;
}

export interface ByProjectReport {
  totalMinutes: number;
  billableMinutes: number;
  totalCost: number;
  byUser: UserBreakdown[];
}

export interface ByClientReport {
  totalMinutes: number;
  billableMinutes: number;
  totalCost: number;
  byProject: ProjectBreakdown[];
}

export interface ByUserReport {
  totalMinutes: number;
  totalCost: number;
  byClientProject: ClientProjectBreakdown[];
}

export interface TimelineBucket {
  bucket: string;
  totalMinutes: number;
}

export function useReportByProject(projectId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: reportKeys.byProject(projectId || "", from, to),
    queryFn: async () =>
      (await apiRequest<ByProjectReport>(`reports/by-project?projectId=${projectId}&from=${from}&to=${to}`)).data,
    enabled: !!projectId,
  });
}

export function useReportByClient(clientId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: reportKeys.byClient(clientId || "", from, to),
    queryFn: async () =>
      (await apiRequest<ByClientReport>(`reports/by-client?clientId=${clientId}&from=${from}&to=${to}`)).data,
    enabled: !!clientId,
  });
}

export function useReportByUser(userId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: reportKeys.byUser(userId || "", from, to),
    queryFn: async () =>
      (await apiRequest<ByUserReport>(`reports/by-user?userId=${userId}&from=${from}&to=${to}`)).data,
    enabled: !!userId,
  });
}

export function useReportTimeline(
  scope: "project" | "client" | undefined,
  scopeId: string | undefined,
  from: string,
  to: string,
  granularity: "day" | "week" | "month"
) {
  return useQuery({
    queryKey: reportKeys.timeline(scope || "", scopeId || "", from, to, granularity),
    queryFn: async () =>
      (
        await apiRequest<TimelineBucket[]>(
          `reports/timeline?scope=${scope}&scopeId=${scopeId}&from=${from}&to=${to}&granularity=${granularity}`
        )
      ).data,
    enabled: !!scope && !!scopeId,
  });
}

export interface VelocityPoint {
  sprintId: string;
  sprintName: string;
  completedPoints: number;
}

export function useReportVelocity(projectId: string | undefined) {
  return useQuery({
    queryKey: reportKeys.velocity(projectId || ""),
    queryFn: async () => (await apiRequest<VelocityPoint[]>(`reports/velocity?projectId=${projectId}`)).data,
    enabled: !!projectId,
  });
}
